import { formatOffsetTime, parseOffsetTime } from '@agentic-flow-studio/shared/lib/job-plan-time';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agentic-flow-studio/shared/ui/select';
import {
  ArrowLeft,
  Check,
  Clock,
  Loader2,
  MapPin,
  Plus,
  Power,
  Shield,
  User,
  Wrench,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface OperatorFormPageProps {
  mode: 'create' | 'edit';
}

export default function OperatorFormPage({ mode }: OperatorFormPageProps) {
  const { orgId, workspaceId, operatorId } = useParams<{
    orgId: string;
    workspaceId: string;
    operatorId?: string;
  }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const basePath = `/org/${orgId}/workspace/${workspaceId}/operators`;

  // Form states
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [startLocationId, setStartLocationId] = useState<string>('default');
  const [endLocationMode, setEndLocationMode] = useState<string>('same_as_start');
  const [shiftStart, setShiftStart] = useState('08:00');
  const [shiftEnd, setShiftEnd] = useState('17:00');
  const [breakDurationMins, setBreakDurationMins] = useState(30);
  const [breakWindowStart, setBreakWindowStart] = useState('12:00');
  const [breakWindowEnd, setBreakWindowEnd] = useState('14:00');
  const [isActive, setIsActive] = useState(true);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);

  // Inline new skill input
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillDescription, setNewSkillDescription] = useState('');

  // Data queries
  const { data: members = [] } = trpc.workspace.listMembers.useQuery();
  const { data: operators = [] } = trpc.jobPlan.operators.list.useQuery();
  const { data: skills = [], isLoading: isSkillsLoading } = trpc.jobPlan.skills.list.useQuery();
  const { data: locations = [] } = trpc.jobPlan.locations.list.useQuery();

  const numOperatorId = operatorId ? parseInt(operatorId, 10) : 0;
  const { data: existingOperator, isLoading: isOperatorLoading } =
    trpc.jobPlan.operators.getProfile.useQuery(
      { id: numOperatorId },
      { enabled: mode === 'edit' && numOperatorId > 0 }
    );

  // Populate form on edit
  useEffect(() => {
    if (mode === 'edit' && existingOperator) {
      setSelectedUserId(String(existingOperator.userId));
      setStartLocationId(
        existingOperator.startLocationId ? String(existingOperator.startLocationId) : 'default'
      );
      if (existingOperator.endLocationId == null) {
        setEndLocationMode('last_stop');
      } else if (
        existingOperator.startLocationId != null &&
        existingOperator.endLocationId === existingOperator.startLocationId
      ) {
        setEndLocationMode('same_as_start');
      } else {
        setEndLocationMode(String(existingOperator.endLocationId));
      }
      setShiftStart(formatOffsetTime(existingOperator.shiftStartOffsetSeconds));
      setShiftEnd(formatOffsetTime(existingOperator.shiftEndOffsetSeconds));
      setBreakDurationMins(Math.round(existingOperator.breakDurationSeconds / 60));
      setBreakWindowStart(
        existingOperator.breakWindowStartOffsetSeconds != null
          ? formatOffsetTime(existingOperator.breakWindowStartOffsetSeconds)
          : '12:00'
      );
      setBreakWindowEnd(
        existingOperator.breakWindowEndOffsetSeconds != null
          ? formatOffsetTime(existingOperator.breakWindowEndOffsetSeconds)
          : '14:00'
      );
      setIsActive(existingOperator.isActive);
      setSelectedSkillIds((existingOperator.skills ?? []).map((s) => s.id));
    }
  }, [mode, existingOperator]);

  const createSkillMutation = trpc.jobPlan.skills.create.useMutation({
    onSuccess: (newSkill) => {
      toast.success(`Skill "${newSkill.name}" created`);
      setNewSkillName('');
      setNewSkillDescription('');
      utils.jobPlan.skills.list.invalidate();
      setSelectedSkillIds((prev) => [...prev, newSkill.id]);
    },
    onError: (err) => toast.error(err.message),
  });

  const createOperatorMutation = trpc.jobPlan.operators.createProfile.useMutation({
    onSuccess: () => {
      toast.success('Operator profile created');
      utils.jobPlan.operators.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateOperatorMutation = trpc.jobPlan.operators.updateProfile.useMutation({
    onSuccess: () => {
      toast.success('Operator profile updated');
      utils.jobPlan.operators.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleQuickAddSkill = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;
    createSkillMutation.mutate({
      name: newSkillName.trim(),
      description: newSkillDescription.trim() || undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const startLocId =
      startLocationId && startLocationId !== 'default' ? Number(startLocationId) : null;
    const resolvedEndLocationId =
      endLocationMode === 'last_stop'
        ? null
        : endLocationMode === 'same_as_start'
          ? startLocId
          : Number(endLocationMode);

    if (mode === 'create') {
      if (!selectedUserId) {
        toast.error('Please select a team member');
        return;
      }

      createOperatorMutation.mutate({
        userId: Number(selectedUserId),
        startLocationId: startLocId,
        endLocationId: resolvedEndLocationId,
        shiftStartOffsetSeconds: parseOffsetTime(shiftStart),
        shiftEndOffsetSeconds: parseOffsetTime(shiftEnd),
        breakDurationSeconds: breakDurationMins * 60,
        breakWindowStartOffsetSeconds: parseOffsetTime(breakWindowStart),
        breakWindowEndOffsetSeconds: parseOffsetTime(breakWindowEnd),
        isActive,
        skillIds: selectedSkillIds,
      });
    } else {
      if (!numOperatorId) return;

      updateOperatorMutation.mutate({
        id: numOperatorId,
        startLocationId: startLocId,
        endLocationId: resolvedEndLocationId,
        shiftStartOffsetSeconds: parseOffsetTime(shiftStart),
        shiftEndOffsetSeconds: parseOffsetTime(shiftEnd),
        breakDurationSeconds: breakDurationMins * 60,
        breakWindowStartOffsetSeconds: parseOffsetTime(breakWindowStart),
        breakWindowEndOffsetSeconds: parseOffsetTime(breakWindowEnd),
        isActive,
        skillIds: selectedSkillIds,
      });
    }
  };

  // Filter available members in create mode
  const existingUserIds = new Set(operators.map((op) => op.userId));
  const availableMembers = members.filter((m) => !existingUserIds.has(m.id));

  const isSaving = createOperatorMutation.isPending || updateOperatorMutation.isPending;

  if (mode === 'edit' && isOperatorLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading operator profile...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Sticky Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Link to={basePath}>
              <ArrowLeft className="h-4 w-4" />
              <span>Operators</span>
            </Link>
          </Button>

          <span className="text-muted-foreground/50">/</span>

          <h1 className="text-lg font-bold tracking-tight text-foreground">
            {mode === 'create'
              ? 'New Operator Profile'
              : `Edit ${existingOperator?.user?.fullName || existingOperator?.user?.email || 'Operator'}`}
          </h1>

          {mode === 'edit' && (
            <Badge
              variant="outline"
              className={
                isActive
                  ? 'text-xs text-emerald-600 bg-emerald-500/10 border-emerald-500/20'
                  : 'text-xs text-muted-foreground bg-muted border-border'
              }
            >
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2.5">
          <Button variant="outline" size="sm" asChild disabled={isSaving}>
            <Link to={basePath}>Cancel</Link>
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSaving || (mode === 'create' && !selectedUserId)}
            className="gap-1.5"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{mode === 'create' ? 'Create Operator Profile' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>

      {/* Main Full Page Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-4xl w-full mx-auto space-y-6">
        {/* Section 1: Team Member Identity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <span>Team Member Identity</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Link this operator profile to an active user account in your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {mode === 'create' ? (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Workspace Member *</label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a team member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.fullName ? `${m.fullName} (${m.email})` : m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableMembers.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    All existing workspace members already have operator profiles configured.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-muted/20">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                  {(existingOperator?.user?.fullName ||
                    existingOperator?.user?.email ||
                    'U')[0].toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-sm">
                    {existingOperator?.user?.fullName || existingOperator?.user?.email}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {existingOperator?.user?.email}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Shift Schedule & Working Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span>Shift Schedule & Breaks</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Define the operator's daily working hours, mandatory rest break duration, and
              preferred rest windows for route planning.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Shift Start Time</label>
                <Input
                  type="time"
                  value={shiftStart}
                  onChange={(e) => setShiftStart(e.target.value)}
                />
                <span className="text-[11px] text-muted-foreground">
                  Earliest departure time from start depot.
                </span>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Shift End Time</label>
                <Input type="time" value={shiftEnd} onChange={(e) => setShiftEnd(e.target.value)} />
                <span className="text-[11px] text-muted-foreground">
                  Latest arrival time at destination depot.
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-border/40">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Break Duration (mins)</label>
                <Input
                  type="number"
                  min={0}
                  step={5}
                  value={breakDurationMins}
                  onChange={(e) => setBreakDurationMins(Number(e.target.value))}
                />
                <span className="text-[11px] text-muted-foreground">
                  Total continuous rest time.
                </span>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Break Window Start</label>
                <Input
                  type="time"
                  value={breakWindowStart}
                  onChange={(e) => setBreakWindowStart(e.target.value)}
                />
                <span className="text-[11px] text-muted-foreground">
                  Earliest rest period start.
                </span>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Break Window End</label>
                <Input
                  type="time"
                  value={breakWindowEnd}
                  onChange={(e) => setBreakWindowEnd(e.target.value)}
                />
                <span className="text-[11px] text-muted-foreground">Latest rest period end.</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Depot & Start/End Locations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Depot & Base Locations</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Where this operator starts and concludes their route each day.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  Start Depot / Location
                </label>
                <Select value={startLocationId} onValueChange={setStartLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default Hub / First Job</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={String(loc.id)}>
                        {loc.label} {loc.formattedAddress ? `(${loc.formattedAddress})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">
                    End Depot / Location
                  </label>
                  <Badge
                    variant="secondary"
                    className="text-[10px] py-0 px-1.5 font-normal bg-muted text-muted-foreground"
                  >
                    {endLocationMode === 'last_stop'
                      ? 'Open Route'
                      : endLocationMode === 'same_as_start'
                        ? 'Round Trip'
                        : 'Specific Hub'}
                  </Badge>
                </div>
                <Select value={endLocationMode} onValueChange={setEndLocationMode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select end location..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_stop">
                      🏁 Finish at Last Stop (Open Route / Anywhere)
                    </SelectItem>
                    <SelectItem value="same_as_start">
                      🏠 Return to Start Depot (Round Trip)
                    </SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={String(loc.id)}>
                        🏢 {loc.label} {loc.formattedAddress ? `(${loc.formattedAddress})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-muted-foreground block">
                  {endLocationMode === 'last_stop'
                    ? 'Shift ends immediately at the final job site without scheduling return travel.'
                    : endLocationMode === 'same_as_start'
                      ? 'Operator returns to their starting depot at the conclusion of their shift.'
                      : 'Operator travels to this specific hub/workshop at the end of their shift.'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Skills & Certifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-primary" />
                  <span>Assigned Skills & Certifications</span>
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  The solver strictly pairs jobs with operators whose skills fulfill all required
                  job tags.
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs font-medium">
                {selectedSkillIds.length} Selected
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Catalog skills multi-select */}
            {isSkillsLoading ? (
              <div className="text-xs text-muted-foreground py-4 text-center">
                Loading skills...
              </div>
            ) : skills.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                {skills.map((skill) => {
                  const isSelected = selectedSkillIds.includes(skill.id);
                  return (
                    <div
                      key={skill.id}
                      onClick={() => {
                        setSelectedSkillIds((prev) =>
                          isSelected ? prev.filter((id) => id !== skill.id) : [...prev, skill.id]
                        );
                      }}
                      className={`cursor-pointer rounded-lg border p-3 flex items-start gap-2.5 transition-all select-none ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                          : 'border-border/60 bg-card hover:bg-muted/40'
                      }`}
                    >
                      <div
                        className={`h-4 w-4 rounded mt-0.5 flex items-center justify-center border transition-colors ${
                          isSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-muted-foreground/40 bg-background'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <div className="flex flex-col flex-1 overflow-hidden">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs text-foreground truncate">
                            {skill.name}
                          </span>
                          {skill.color && (
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: skill.color }}
                            />
                          )}
                        </div>
                        {skill.description && (
                          <span className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                            {skill.description}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-lg">
                <Shield className="h-6 w-6 mx-auto mb-1 opacity-40 text-muted-foreground" />
                <span>No skills created in catalog yet. Create one below to assign.</span>
              </div>
            )}

            {/* Inline Quick Add Skill Box */}
            <div className="p-4 rounded-lg border border-border/60 bg-muted/20 space-y-3">
              <div className="flex items-center gap-2">
                <Plus className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold text-foreground">
                  Add New Skill to Catalog
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  placeholder="Skill name (e.g. Forklift Certified, Level 2 Electrical)..."
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  className="text-xs h-8"
                />
                <Input
                  placeholder="Optional description or requirements..."
                  value={newSkillDescription}
                  onChange={(e) => setNewSkillDescription(e.target.value)}
                  className="text-xs h-8"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-7 text-xs gap-1.5"
                  disabled={!newSkillName.trim() || createSkillMutation.isPending}
                  onClick={handleQuickAddSkill}
                >
                  {createSkillMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                  <span>Create & Assign Skill</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Operator Status / Active Toggle */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Power className="h-4 w-4 text-primary" />
              <span>Operator Availability Status</span>
            </CardTitle>
            <CardDescription className="text-xs">
              When deactivated, this operator will not be scheduled for new runs and route
              optimization will ignore them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/20">
              <div className="flex flex-col">
                <span className="font-semibold text-xs text-foreground">Active for Job Plan</span>
                <span className="text-[11px] text-muted-foreground">
                  {isActive
                    ? 'Eligible to be assigned routes and scheduled jobs.'
                    : 'Paused / Inactive. Will not receive routes.'}
                </span>
              </div>

              <Button
                type="button"
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setIsActive((prev) => !prev)}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-end gap-3 pt-4 pb-12 border-t border-border/40">
          <Button variant="outline" asChild disabled={isSaving}>
            <Link to={basePath}>Cancel</Link>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || (mode === 'create' && !selectedUserId)}
            className="gap-2"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{mode === 'create' ? 'Create Operator Profile' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
